import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Modal, Form, Alert, Placeholder, Dropdown } from 'react-bootstrap';
import { getPortfolios, createPortfolio, getUserProfile, updatePortfolio, getTickers, getLastUpdateTime } from '../services/api';
import { Link } from 'react-router-dom';
import AccountPortfolioTable from '../components/AccountPortfolioTable';
import ColumnVisibilityDropdown from '../components/ColumnVisibilityDropdown';
import PieChartModal from '../components/PieChartModal';
import BalanceHistoryModal from '../components/BalanceHistoryModal';
import { useTickerUpdates } from '../hooks/useTickerUpdates';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { formatBalanceDisplay, normalizeNearZero } from '../utils/formatBalance';
import { createLogger } from '../utils/debugLogger';

const logger = createLogger(
  'dashboardPage',
  'Dashboard page ticker refreshes and portfolio management',
  false,
  'DEBUG'
);

const MARKET_STATUS_STORAGE_KEY = 'allAccounts-marketClosed';

const readStoredMarketClosedStatus = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return false;
  }
  try {
    return window.localStorage.getItem(MARKET_STATUS_STORAGE_KEY) === 'true';
  } catch (error) {
    return false;
  }
};

const persistMarketClosedStatus = (isClosed) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  try {
    window.localStorage.setItem(MARKET_STATUS_STORAGE_KEY, isClosed ? 'true' : 'false');
  } catch (error) {
    // Ignore storage errors; runtime state still updates
  }
};

// Custom styles for the component - following accounts page pattern
const customStyles = `
    /* Enhanced card styling for maximum contrast and prominence */
    .portfolios-card {
        background-color: var(--card-bg) !important;
        border: 2px solid var(--border-color) !important;
        border-radius: 8px !important;
        box-shadow: 0 4px 16px var(--shadow-color) !important;
        transition: all 0.3s ease-in-out !important;
    }
    
    .portfolios-card:hover {
        transform: translateY(-2px) !important;
        box-shadow: 0 8px 24px var(--shadow-color) !important;
    }
    
    [data-theme="light"] .portfolios-card {
        background-color: #ffffff !important;
        border-color: #d1d5db !important;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1) !important;
    }
    
    [data-theme="light"] .portfolios-card:hover {
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15) !important;
    }
    
    [data-theme="dark"] .portfolios-card {
        background-color: #1f2937 !important;
        border-color: #4b5563 !important;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3) !important;
    }
    
    [data-theme="dark"] .portfolios-card:hover {
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4) !important;
    }
    
    /* Enhanced table container styling */
    .portfolios-table-container {
        background-color: var(--card-bg) !important;
        border: 2px solid var(--border-color) !important;
        border-radius: 8px !important;
        box-shadow: 0 4px 16px var(--shadow-color) !important;
        overflow: hidden !important;
        margin-bottom: 1rem !important;
    }
    
    [data-theme="light"] .portfolios-table-container {
        background-color: #ffffff !important;
        border-color: #d1d5db !important;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1) !important;
    }
    
    [data-theme="dark"] .portfolios-table-container {
        background-color: #1f2937 !important;
        border-color: #4b5563 !important;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3) !important;
    }
    
    /* Enhanced table styling */
    .portfolios-table-container .account-portfolio-table {
        background-color: var(--card-bg) !important;
        border: none !important;
        margin-bottom: 0 !important;
    }
    
    /* Remove gap between last row and bottom of table container */
    .portfolios-table-container .account-portfolio-table {
        margin-bottom: 0 !important;
    }
    
    /* Enhanced table header styling */
    .portfolios-table-container .account-portfolio-table thead th,
    .portfolios-table-container .portfolios-table-header {
        background-color: var(--bg-secondary) !important;
        border-bottom: 2px solid var(--border-color) !important;
        font-weight: 600 !important;
        color: var(--text-primary) !important;
        padding: 6px 6px !important;
        transition: background-color 0.2s ease-in-out;
    }
    
    .portfolios-table-container .account-portfolio-table thead th:hover,
    .portfolios-table-container .portfolios-table-header:hover {
        background-color: var(--hover-bg) !important;
        cursor: pointer;
    }
    
    [data-theme="light"] .portfolios-table-container .account-portfolio-table thead th,
    [data-theme="light"] .portfolios-table-container .portfolios-table-header {
        background-color: #f8f9fa !important;
        border-bottom: 2px solid #d1d5db !important;
        color: #374151 !important;
    }
    
    [data-theme="light"] .portfolios-table-container .account-portfolio-table thead th:hover,
    [data-theme="light"] .portfolios-table-container .portfolios-table-header:hover {
        background-color: #e9ecef !important;
    }
    
    [data-theme="dark"] .portfolios-table-container .account-portfolio-table thead th,
    [data-theme="dark"] .portfolios-table-container .portfolios-table-header {
        background-color: #374151 !important;
        border-bottom: 2px solid #4b5563 !important;
        color: #f9fafb !important;
    }
    
    [data-theme="dark"] .portfolios-table-container .account-portfolio-table thead th:hover,
    [data-theme="dark"] .portfolios-table-container .portfolios-table-header:hover {
        background-color: #4b5563 !important;
    }
    
    /* Enhanced table row styling with hover effects - more prominent and brighter */
    .portfolios-table-container .account-portfolio-table tbody tr {
        border-bottom: 1px solid var(--border-color) !important;
        transition: all 0.3s ease-in-out !important;
        font-size: 0.95rem !important;
    }
    
    .portfolios-table-container .account-portfolio-table tbody tr:hover {}
    [data-theme="light"] .portfolios-table-container .account-portfolio-table tbody tr:hover {}
    [data-theme="dark"] .portfolios-table-container .account-portfolio-table tbody tr:hover {}
    
    /* Enhanced table cell styling - more compact with hover effects */
    .portfolios-table-container .account-portfolio-table tbody td {
        padding: 4px 6px !important;
        border-bottom: 1px solid var(--border-color) !important;
        vertical-align: middle !important;
        transition: all 0.3s ease-in-out !important;
    }
    
    .portfolios-table-container .account-portfolio-table tbody tr:hover td {
        color: var(--text-primary) !important;
        font-weight: 500 !important;
    }
    
    [data-theme="light"] .portfolios-table-container .account-portfolio-table tbody td {
        border-bottom: 1px solid #e5e7eb !important;
    }
    
    [data-theme="light"] .portfolios-table-container .account-portfolio-table tbody tr:hover td {
        color: #1e40af !important;
    }
    
    [data-theme="dark"] .portfolios-table-container .account-portfolio-table tbody td {
        border-bottom: 1px solid #374151 !important;
    }
    
    [data-theme="dark"] .portfolios-table-container .account-portfolio-table tbody tr:hover td {
        color: #93c5fd !important;
    }
    
    /* Enhanced total row styling - match header styling exactly */
    .portfolios-table-container .account-portfolio-table .table-total {
        background-color: var(--bg-secondary) !important;
        border-top: 2px solid var(--border-color) !important;
        border-bottom: 2px solid var(--border-color) !important;
        font-weight: 600 !important;
        color: var(--text-primary) !important;
    }
    
    .portfolios-table-container .account-portfolio-table .table-total td {
        background-color: var(--bg-secondary) !important;
        color: var(--text-primary) !important;
        padding: 6px 6px !important;
        border-bottom: none !important;
    }
    
    .portfolios-table-container .account-portfolio-table .table-total:hover {
        background-color: var(--hover-bg) !important;
        cursor: default !important;
        transform: none !important;
        box-shadow: none !important;
    }
    
    [data-theme="light"] .portfolios-table-container .account-portfolio-table .table-total {
        background-color: #f8f9fa !important;
        border-top: 2px solid #d1d5db !important;
        border-bottom: 2px solid #d1d5db !important;
        color: #374151 !important;
    }
    
    [data-theme="light"] .portfolios-table-container .account-portfolio-table .table-total td {
        background-color: #f8f9fa !important;
        color: #374151 !important;
    }
    
    [data-theme="light"] .portfolios-table-container .account-portfolio-table .table-total:hover {
        background-color: #e9ecef !important;
    }
    
    [data-theme="dark"] .portfolios-table-container .account-portfolio-table .table-total {
        background-color: #374151 !important;
        border-top: 2px solid #4b5563 !important;
        border-bottom: 2px solid #4b5563 !important;
        color: #f9fafb !important;
    }
    
    [data-theme="dark"] .portfolios-table-container .account-portfolio-table .table-total td {
        background-color: #374151 !important;
        color: #f9fafb !important;
    }
    
    [data-theme="dark"] .portfolios-table-container .account-portfolio-table .table-total:hover {
        background-color: #4b5563 !important;
    }
    
    /* CSS Variables for theme support */
    :root {
        --card-bg: #ffffff;
        --border-color: #d1d5db;
        --shadow-color: rgba(0, 0, 0, 0.1);
        --text-primary: #374151;
        --bg-secondary: #f8f9fa;
        --hover-bg: #f1f5f9;
    }
    
    [data-theme="dark"] {
        --card-bg: #1f2937;
        --border-color: #4b5563;
        --shadow-color: rgba(0, 0, 0, 0.3);
        --text-primary: #f9fafb;
        --bg-secondary: #374151;
        --hover-bg: #4b5563;
    }
    
    /* Portfolio card specific styling */
    .portfolio-card {
        transition: all 0.15s ease-in-out !important;
        cursor: pointer !important;
        min-height: 140px !important;
        border-width: 1px !important;
    }
    
    .clickable-portfolio-card:hover {
        transform: translateY(-2px) !important;
        box-shadow: 0 6px 20px var(--shadow-color) !important;
        border-width: 1.5px !important;
    }
    
    [data-theme="light"] .clickable-portfolio-card:hover {
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12) !important;
    }
    
    [data-theme="dark"] .clickable-portfolio-card:hover {
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.35) !important;
    }
    
    /* Cards container styling */
    .portfolios-cards-container {
        margin-bottom: 1rem;
    }
    
    /* Compact spacing adjustments */
    .portfolio-card .card-body .row {
        margin: 0 !important;
    }
    
    .portfolio-card .card-body .row > * {
        padding-left: 1px !important;
        padding-right: 1px !important;
    }
    
    /* Remove link color changes on hover for cards */
    .portfolio-card a:hover {
        color: inherit !important;
        text-decoration: none !important;
    }
    
    /* Ensure card content doesn't affect link styling */
    .portfolio-card .card-title a {
        color: inherit !important;
    }
`;

function Dashboard({ userId }) {
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPortfolioName, setNewPortfolioName] = useState('');
  const [newPortfolioDescription, setNewPortfolioDescription] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [, setUserProfile] = useState(null);
  const [showPieChartModal, setShowPieChartModal] = useState(false);
  const [showBalanceHistoryModal, setShowBalanceHistoryModal] = useState(false);
  const marketClosedRef = useRef(readStoredMarketClosedStatus());
  const updateMarketClosedState = useCallback((nextValue) => {
    if (typeof nextValue !== 'boolean') {
      return false;
    }
    const previous = marketClosedRef.current;
    if (previous === nextValue) {
      return false;
    }
    marketClosedRef.current = nextValue;
    persistMarketClosedStatus(nextValue);
    return previous && !nextValue;
  }, []);
  
  // View mode state - default to table view
  const [viewMode, setViewMode] = useState(() => {
    const savedViewMode = localStorage.getItem('portfoliosViewMode');
    return savedViewMode || 'table';
  });
  
  // Edit portfolio state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editType, setEditType] = useState('name'); // 'name' or 'description'
  const [editPortfolioId, setEditPortfolioId] = useState(null);
  const [editPortfolioValue, setEditPortfolioValue] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  // Default column order
  const defaultColumnOrder = [
    'portfolio',
    'description', 
    'balance',
    'cash',
    'today_gain_amount',
    'today_gain_percentage',
    'total_gain_amount',
    'total_gain_percentage',
    'ytd_gain_amount',
    'ytd_gain_percentage'
  ];
  
  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const savedColumns = localStorage.getItem(`visibleColumns-dashboard`);
    if (savedColumns) {
      const parsed = JSON.parse(savedColumns);
      return {
        ...parsed,
        // Ensure these columns are always present if not in localStorage
        portfolio: parsed.portfolio !== undefined ? parsed.portfolio : true,
        balance: parsed.balance !== undefined ? parsed.balance : true,
        cash: parsed.cash !== undefined ? parsed.cash : true,
        today_gain_percentage: parsed.today_gain_percentage !== undefined ? parsed.today_gain_percentage : true,
        ytd_gain_percentage: parsed.ytd_gain_percentage !== undefined ? parsed.ytd_gain_percentage : true
      };
    }
    return {
      portfolio: true,
      description: true,
      balance: true,
      cash: true,
      today_gain_amount: true,
      today_gain_percentage: true,
      total_gain_amount: true,
      total_gain_percentage: true,
      ytd_gain_amount: true,
      ytd_gain_percentage: true
    };
  });
  
  // Column order state
  const [columnOrder, setColumnOrder] = useState(() => {
    const savedOrder = localStorage.getItem(`columnOrder-dashboard`);
    return savedOrder ? JSON.parse(savedOrder) : defaultColumnOrder;
  });
  
  // Save column visibility settings when they change
  useEffect(() => {
    localStorage.setItem(`visibleColumns-dashboard`, JSON.stringify(visibleColumns));
  }, [visibleColumns]);
  
  // Save column order when it changes
  useEffect(() => {
    localStorage.setItem(`columnOrder-dashboard`, JSON.stringify(columnOrder));
  }, [columnOrder]);

  // Save view mode when it changes
  useEffect(() => {
    localStorage.setItem('portfoliosViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    let cancelled = false;
    const initializeMarketStatus = async () => {
      try {
        const response = await getLastUpdateTime();
        if (!cancelled && response?.data && typeof response.data.market_closed === 'boolean') {
          updateMarketClosedState(response.data.market_closed);
        }
      } catch (error) {
        // Ignore initialization failures; ticker events will keep status in sync later
      }
    };
    initializeMarketStatus();
    return () => {
      cancelled = true;
    };
  }, [updateMarketClosedState]);

  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await getUserProfile();
      setUserProfile(response.data);
    } catch (err) {
      logger.error('Error fetching user profile', err);
    }
  }, []);

  const fetchPortfolios = useCallback(async () => {
    setTableLoading(true);
    setError('');
    
    try {
      if (!userId) {
        throw new Error('User ID is not available');
      }

      logger.debug('Fetching portfolios for user', { userId });
      const response = await getPortfolios(userId);
      logger.debug('Portfolios response received', response);
      setPortfolios(response.data);
      // Broadcast portfolios for global sync (nav + other pages)
      window.dispatchEvent(new CustomEvent('portfoliosUpdated', { detail: { portfolios: response.data } }));
    } catch (err) {
      logger.error('Error fetching portfolios', err);
      logger.error('Error details', {
        message: err.message,
        response: err.response,
        request: err.request
      });
      
      if (err.response) {
        setError(`Failed to load portfolios: ${err.response.data.error || 'Unknown error'}`);
      } else if (err.request) {
        setError('No response received from server. Please check your connection.');
      } else {
        setError(`Error: ${err.message}`);
      }
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  }, [userId]);

  const fetchLastUpdateTime = useCallback(async () => {
    try {
      const response = await getLastUpdateTime();
      if (response?.data) {
        if (typeof response.data.market_closed === 'boolean') {
          updateMarketClosedState(response.data.market_closed);
        }

        if (response.data.last_update_time) {
          logger.debug('Latest ticker update time', { lastUpdated: response.data.last_update_time });
          window.dispatchEvent(new CustomEvent('tickerUpdate', {
            detail: {
              time: new Date(response.data.last_update_time),
              source: 'Dashboard'
            }
          }));
          return;
        }
      }

      // Fallback: derive timestamp from tickers if API didn't return one
      const fallbackResponse = await getTickers();
      if (fallbackResponse.data && fallbackResponse.data.length > 0) {
        const latestTicker = fallbackResponse.data.reduce((latest, ticker) => {
          if (!latest.last_updated) return ticker;
          if (!ticker.last_updated) return latest;
          
          const latestTime = new Date(latest.last_updated);
          const tickerTime = new Date(ticker.last_updated);
          
          return tickerTime > latestTime ? ticker : latest;
        }, fallbackResponse.data[0]);
        
        if (latestTicker && latestTicker.last_updated) {
          window.dispatchEvent(new CustomEvent('tickerUpdate', {
            detail: {
              time: new Date(latestTicker.last_updated),
              source: 'Dashboard'
            }
          }));
        }
      }
    } catch (err) {
      logger.error('Error fetching last update time', err);
    }
  }, [updateMarketClosedState]);

  useEffect(() => {
    fetchUserProfile();
  }, [userId, fetchUserProfile]);

  useEffect(() => {
    fetchPortfolios();
    fetchLastUpdateTime();
  }, [fetchPortfolios, fetchLastUpdateTime]);

  // Keep in sync with global portfoliosUpdated events (e.g., when nav fetches)
  useEffect(() => {
    const handler = (event) => {
      const incoming = event?.detail?.portfolios;
      if (Array.isArray(incoming)) {
        setPortfolios(incoming);
      }
    };
    window.addEventListener('portfoliosUpdated', handler);
    return () => window.removeEventListener('portfoliosUpdated', handler);
  }, []);

  // Use combined ticker updates (WebSocket + Polling)
  useTickerUpdates({
    enablePolling: true,
    onUpdate: (data) => {
      logger.debug('Dashboard: Received ticker update', {
        hasUpdatedTickers: Boolean(data?.updated_tickers?.length > 0),
        updateCount: data?.updated_tickers?.length || 0,
        source: data?.source || 'unknown',
        hasLastUpdateTime: Boolean(data?.latest_update_time),
        isTimestampEvent: data?.message === 'Timestamp update event'
      });
      
      const reopened = updateMarketClosedState(data?.market_closed);

      if (marketClosedRef.current) {
        logger.debug('Dashboard: Skipping portfolio refresh - market is closed');
        return;
      }

      if (reopened) {
        logger.debug('Dashboard: Market reopened - refreshing portfolios');
        fetchPortfolios();
        return;
      }

      const hasTickerUpdates = Boolean(data?.updated_tickers?.length > 0);
      const shouldRefresh = data?.pollingCycleComplete || hasTickerUpdates;

      if (shouldRefresh) {
        logger.debug(
          `Dashboard: Refreshing portfolio data after polling cycle (${hasTickerUpdates ? 'ticker updates present' : 'no ticker updates'}) from ${data?.source || 'unknown'}`
        );
        fetchPortfolios();
      }
    },
    onStatusChange: (status) => {
      logger.debug('Dashboard: Update status changed', status);
    }
  });

  // Process portfolios to fix gain calculation issues with cash accounts
  const processedPortfolios = useMemo(() => {
    if (!portfolios || portfolios.length === 0) return [];
    
    return portfolios.map(portfolio => {
      // Clone the portfolio to avoid mutating the original
      const processedPortfolio = {...portfolio};
      
      // The backend now handles cash-only portfolios with no previous day balance
      // by setting today's gain to 0, so we don't need to modify it here
      
      return processedPortfolio;
    });
  }, [portfolios]);

  // Memoize filtered portfolios to prevent unnecessary re-renders
  const filteredPortfolios = useMemo(() => {
    return portfolios.filter(portfolio => portfolio.balance > 0);
  }, [portfolios]);

  const handleCreatePortfolio = async (e) => {
    e.preventDefault();
    
    if (!newPortfolioName.trim()) {
      setCreateError('Portfolio name is required');
      return;
    }
    
    setCreateError('');
    setCreateLoading(true);
    
    try {
      await createPortfolio({
        user_id: userId,
        name: newPortfolioName.trim(),
        description: newPortfolioDescription.trim()
      });
      
      // Refresh the portfolios list
      await fetchPortfolios();
      
      // Reset form and close modal
      setNewPortfolioName('');
      setNewPortfolioDescription('');
      setShowCreateModal(false);
    } catch (err) {
      logger.error('Error creating portfolio', err);

      if (err.response && err.response.data && err.response.data.error) {
        setCreateError(err.response.data.error);
      } else {
        setCreateError('Failed to create portfolio. Please try again.');
      }
    } finally {
      setCreateLoading(false);
    }
  };

  // Helper function to safely calculate values when data is loading
  const safeCalculate = (calculationFn) => {
    if (loading || processedPortfolios.length === 0) {
      return 0;
    }
    return calculationFn();
  };

  const calculateTotalBalance = useCallback(() => {
    // Use the same calculation method as NavigationPanel for consistency
    // Sum account balances instead of portfolio balances to avoid discrepancies
    if (!processedPortfolios || processedPortfolios.length === 0) return 0;

    return processedPortfolios.reduce((total, portfolio) => {
      if (portfolio.accounts && portfolio.accounts.length > 0) {
        return total + portfolio.accounts.reduce((portfolioTotal, account) => {
          return portfolioTotal + (account.balance || 0);
        }, 0);
      }
      return total;
    }, 0);
  }, [processedPortfolios]);

  const calculateTotalCash = useCallback(() => {
    // Use the same calculation method as NavigationPanel for consistency
    if (!processedPortfolios || processedPortfolios.length === 0) return 0;

    return processedPortfolios.reduce((sum, portfolio) => {
      const portfolioCash = portfolio.accounts ? portfolio.accounts.reduce((s, account) => s + (account.cash || 0), 0) : 0;
      return sum + portfolioCash;
    }, 0);
  }, [processedPortfolios]);

  // Updated to use real today's gain amounts
  const calculateTotalGain = useCallback(() => {
    // Just sum up the today's gain amounts from all portfolios
    return processedPortfolios.reduce((total, portfolio) => total + portfolio.today_gain_amount, 0);
  }, [processedPortfolios]);

  const calculateOverallGain = useCallback(() => {
    return processedPortfolios.reduce((total, portfolio) => total + (portfolio.total_gain_amount || 0), 0);
  }, [processedPortfolios]);

  const calculateOverallGainPercentage = useCallback(() => {
    // Denominator is cost basis (balance minus the gain itself), not raw
    // current balance -- matching the backend's own total_gain_percentage
    // formula (portfolios.py) and calculateYtdGainPercentage just below,
    // which already does this correctly. Dividing by balance understated
    // gains (and overstated losses): e.g. $100 grown to $150 is a 50% gain
    // on its $100 cost basis, not $50/$150 = 33%.
    const totalBalance = calculateTotalBalance();
    const overallGain = calculateOverallGain();
    const totalCostBasis = totalBalance - overallGain;
    return totalCostBasis > 0 ? (overallGain / totalCostBasis) * 100 : 0;
  }, [calculateTotalBalance, calculateOverallGain]);

  // Calculate YTD gain amount
  const calculateYtdGain = useCallback(() => {
    return processedPortfolios.reduce((total, portfolio) => total + (portfolio.ytd_gain_amount || 0), 0);
  }, [processedPortfolios]);

  // Calculate YTD gain percentage
  const calculateYtdGainPercentage = useCallback(() => {
    // We need to get all starting balances first
    const totalYtdGain = calculateYtdGain();
    const totalYtdStartingBalance = processedPortfolios.reduce((total, portfolio) => {
      const currentBalance = portfolio.balance || 0;
      const ytdGain = portfolio.ytd_gain_amount || 0;
      return total + (currentBalance - ytdGain);
    }, 0);

    return totalYtdStartingBalance > 0 ? (totalYtdGain / totalYtdStartingBalance) * 100 : 0;
  }, [calculateYtdGain, processedPortfolios]);

  // Portfolio totals for consistent calculation
  const portfolioTotals = useMemo(() => {
    if (!processedPortfolios || processedPortfolios.length === 0) {
      return {
        balance: 0,
        cash: 0,
        today_gain_amount: 0,
        today_gain_percentage: 0,
        total_gain_amount: 0, 
        total_gain_percentage: 0,
        ytd_gain_amount: 0,
        ytd_gain_percentage: 0
      };
    }
    
    const totalBalance = calculateTotalBalance();
    const totalCash = calculateTotalCash();
    const totalTodayGain = calculateTotalGain(); // This now handles the 100% cash case
    const totalPreviousDayBalance = processedPortfolios.reduce((total, portfolio) => 
      total + (portfolio.previous_day_balance || 0), 0);
    
    // Calculate gain percentage, being careful about division by zero
    let todayGainPercentage = 0;
    if (totalPreviousDayBalance > 0) {
      todayGainPercentage = (totalTodayGain / totalPreviousDayBalance) * 100;
    }
    
    const totalOverallGain = calculateOverallGain();
    const overallGainPercentage = calculateOverallGainPercentage();
    const totalYtdGain = calculateYtdGain();
    const ytdGainPercentage = calculateYtdGainPercentage();
    
    return {
      balance: totalBalance,
      cash: totalCash,
      today_gain_amount: totalTodayGain,
      today_gain_percentage: todayGainPercentage,
      total_gain_amount: totalOverallGain,
      total_gain_percentage: overallGainPercentage,
      ytd_gain_amount: totalYtdGain,
      ytd_gain_percentage: ytdGainPercentage
    };
  }, [processedPortfolios, calculateTotalBalance, calculateTotalCash, calculateTotalGain, calculateOverallGain, calculateOverallGainPercentage, calculateYtdGain, calculateYtdGainPercentage]);

  // Calculate total bottom line for all portfolios
  const calculateTotalBottomLine = () => {
    return processedPortfolios.reduce((total, portfolio) => total + (portfolio.bottom_line || 0), 0);
  };

  const handleEditName = (portfolio) => {
    setEditType('name');
    setEditPortfolioId(portfolio.portfolio_id);
    setEditPortfolioValue(portfolio.name);
    setShowEditModal(true);
  };

  const handleEditDescription = (portfolio) => {
    setEditType('description');
    setEditPortfolioId(portfolio.portfolio_id);
    setEditPortfolioValue(portfolio.description || '');
    setShowEditModal(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    
    if (editType === 'name' && !editPortfolioValue.trim()) {
      setEditError('Portfolio name is required');
      return;
    }
    
    setEditError('');
    setEditLoading(true);
    
    try {
      const updateData = {
        [editType]: editPortfolioValue.trim()
      };
      
      await updatePortfolio(editPortfolioId, updateData);
      
      // Update the portfolios list without a full refetch
      setPortfolios(prevPortfolios => 
        prevPortfolios.map(portfolio => 
          portfolio.portfolio_id === editPortfolioId 
            ? { ...portfolio, [editType]: editPortfolioValue.trim() } 
            : portfolio
        )
      );
      
      // Close modal
      setShowEditModal(false);
    } catch (err) {
      logger.error('Error updating portfolio', err);

      if (err.response && err.response.data && err.response.data.error) {
        setEditError(err.response.data.error);
      } else {
        setEditError('Failed to update portfolio. Please try again.');
      }
    } finally {
      setEditLoading(false);
    }
  };

  // PortfolioCard component for cards view
  const PortfolioCard = ({ portfolio }) => {
    const todayGainAmount = portfolio.today_gain_amount || 0;
    const todayGainPct = portfolio.today_gain_percentage || 0;
    const totalGainAmount = portfolio.total_gain_amount || 0;
    const totalGainPct = portfolio.total_gain_percentage || 0;
    const ytdGainAmount = portfolio.ytd_gain_amount || 0;
    const ytdGainPct = portfolio.ytd_gain_percentage || 0;

    // Determine background color based on today's gain
    const getBackgroundStyle = () => {
      if (todayGainAmount === 0) return {};
      
      const isGain = todayGainAmount > 0;
      return {
        backgroundColor: isGain 
          ? 'rgba(25, 135, 84, 0.05)' // Light green for gains
          : 'rgba(220, 53, 69, 0.05)', // Light red for losses
        borderColor: isGain 
          ? 'rgba(25, 135, 84, 0.2)' 
          : 'rgba(220, 53, 69, 0.2)'
      };
    };
    
    return (
      <div className="col-lg-4 col-md-6 col-sm-12 mb-2">
        <Link 
          to={`/portfolios/${portfolio.portfolio_id}`} 
          className="text-decoration-none"
          style={{ color: 'inherit' }}
        >
          <Card 
            className="h-100 portfolios-card portfolio-card clickable-portfolio-card"
            style={getBackgroundStyle()}
          >
            <Card.Body className="p-2">
              <div className="d-flex justify-content-between align-items-start mb-1">
                <div className="flex-grow-1">
                  <Card.Title className="fs-6 mb-0 fw-bold">
                    {portfolio.name}
                  </Card.Title>
                  {portfolio.description && (
                    <Card.Subtitle className="text-muted" style={{ fontSize: '0.75rem' }}>
                      {portfolio.description.length > 25 ? `${portfolio.description.substring(0, 25)}...` : portfolio.description}
                    </Card.Subtitle>
                  )}
                </div>
                <div className="text-end">
                  <div className="text-muted" style={{ fontSize: '0.65rem' }}>
                    {portfolio.accounts ? portfolio.accounts.length : 0} <span className="text-muted">Accounts</span>
                  </div>
                </div>
              </div>
              
              <div className="row g-1 mb-1">
                <div className="col-6">
                  <div className="d-flex align-items-center gap-1">
                    <span className="text-muted" style={{ fontSize: '0.7rem' }}>Balance:</span>
                    <span className="fw-bold small">{formatBalanceDisplay(portfolio.balance)}</span>
                  </div>
                </div>
                <div className="col-6">
                  <div className="d-flex align-items-center gap-1">
                    <span className="text-muted" style={{ fontSize: '0.7rem' }}>Cash:</span>
                    <span className="small">{formatBalanceDisplay(portfolio.cash || 0)}</span>
                  </div>
                </div>
              </div>
              
              <div className="row g-1 text-center">
                <div className="col-4">
                  <div className={`fw-bold ${todayGainAmount >= 0 ? 'text-success' : 'text-danger'}`} style={{ fontSize: '0.8rem' }}>
                    {formatBalanceDisplay(todayGainAmount)}
                  </div>
                  <div className={`${todayGainPct >= 0 ? 'text-success' : 'text-danger'}`} style={{ fontSize: '0.65rem' }}>
                    {todayGainPct >= 0 ? '+' : ''}{todayGainPct.toFixed(1)}%
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.6rem' }}>Today</div>
                </div>
                <div className="col-4">
                  <div className={`fw-bold ${totalGainAmount >= 0 ? 'text-success' : 'text-danger'}`} style={{ fontSize: '0.8rem' }}>
                    {formatBalanceDisplay(totalGainAmount)}
                  </div>
                  <div className={`${totalGainPct >= 0 ? 'text-success' : 'text-danger'}`} style={{ fontSize: '0.65rem' }}>
                    {totalGainPct >= 0 ? '+' : ''}{totalGainPct.toFixed(1)}%
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.6rem' }}>Total</div>
                </div>
                <div className="col-4">
                  <div className={`fw-bold ${ytdGainAmount >= 0 ? 'text-success' : 'text-danger'}`} style={{ fontSize: '0.8rem' }}>
                    {formatBalanceDisplay(ytdGainAmount)}
                  </div>
                  <div className={`${ytdGainPct >= 0 ? 'text-success' : 'text-danger'}`} style={{ fontSize: '0.65rem' }}>
                    {ytdGainPct >= 0 ? '+' : ''}{ytdGainPct.toFixed(1)}%
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.6rem' }}>YTD</div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Link>
      </div>
    );
  };

  return (
    <Container>
      <style>{customStyles}</style>
      {error && (
        <Alert variant="danger">{error}</Alert>
      )}


      {/* Always show summary cards, but with placeholders when loading */}
      {(!loading && portfolios.length > 0) || loading ? (
        <>
          <Row className="mb-4">
            <Col className="d-flex flex-wrap">
              <div className="col-xl col-lg-4 col-md-6 col-sm-6 pb-3 px-1">
                <Card className="h-100 portfolios-card">
                  <Card.Body className="p-2 text-center">
                    <Card.Title className="fs-6 mb-1">Total Balance</Card.Title>
                    <div className="text-muted small" style={{height: "18px"}}></div>
                    {loading ? (
                      <Placeholder as={Card.Text} animation="glow" className="mb-0">
                        <Placeholder xs={8} className="fs-5" />
                      </Placeholder>
                    ) : (
                      <Card.Text className="fs-5 mb-0 fw-bold">
                        {formatBalanceDisplay(safeCalculate(calculateTotalBalance))}
                      </Card.Text>
                    )}
                    <div style={{height: "19px"}}></div>
                  </Card.Body>
                </Card>
              </div>
              {/* Bottom Line Card - 2nd position */}
              <div className="col-xl col-lg-4 col-md-6 col-sm-6 pb-3 px-1">
                <Card className="h-100 portfolios-card">
                  <Card.Body className="p-2 text-center">
                    <Card.Title className="fs-6 mb-1">
                      Bottom Line
                      <i 
                        className="bi bi-info-circle ms-1" 
                        style={{ fontSize: '0.8rem', cursor: 'help' }}
                        title="Bottom line risks calculate with current account value. If you see bigger negative risks, consider raising your stop loss to reduce bottom line risks."
                      ></i>
                    </Card.Title>
                    <div className="text-muted small" style={{height: "18px"}}>If all stop losses hit</div>
                    {loading ? (
                      <Placeholder as={Card.Text} animation="glow" className="mb-0">
                        <Placeholder xs={8} className="fs-5" />
                      </Placeholder>
                    ) : (
                      <>
                        <Card.Text className="fs-5 mb-0 fw-bold">
                          {formatBalanceDisplay(safeCalculate(calculateTotalBottomLine))}
                        </Card.Text>
                        <span className="d-block small text-muted">
                          ({safeCalculate(calculateTotalBalance) > 0 ? (safeCalculate(calculateTotalBottomLine) / safeCalculate(calculateTotalBalance) * 100).toFixed(2) : '0.00'}%)
                          {safeCalculate(calculateTotalBalance) > 0 && (
                            <span
                              style={{
                                color: (() => {
                                  const pct = (safeCalculate(calculateTotalBottomLine) / safeCalculate(calculateTotalBalance)) * 100;
                                  const effect = pct - 100;
                                  if (effect <= -3) return 'red';
                                  if (effect > -3) return 'goldenrod';
                                  return 'inherit';
                                })(),
                                marginLeft: 6,
                              }}
                            >
                              ({(() => {
                                const pct = (safeCalculate(calculateTotalBottomLine) / safeCalculate(calculateTotalBalance)) * 100;
                                const effect = pct - 100;
                                return `${effect >= 0 ? '+' : ''}${effect.toFixed(2)}%`;
                              })()})
                            </span>
                          )}
                        </span>
                      </>
                    )}
                    <div style={{height: "19px"}}></div>
                  </Card.Body>
                </Card>
              </div>
              {/* Cash Balance Card - now 3rd */}
              <div className="col-xl col-lg-4 col-md-6 col-sm-6 pb-3 px-1">
                <Card className="h-100 portfolios-card">
                  <Card.Body className="p-2 text-center">
                    <Card.Title className="fs-6 mb-1">Cash Balance</Card.Title>
                    <div className="text-muted small" style={{height: "18px"}}></div>
                    {loading ? (
                      <Placeholder as={Card.Text} animation="glow" className="mb-0">
                        <Placeholder xs={8} className="fs-5" />
                      </Placeholder>
                    ) : (
                      <Card.Text className="fs-5 mb-0 fw-bold">
                        {formatBalanceDisplay(safeCalculate(calculateTotalCash))}
                      </Card.Text>
                    )}
                    {loading ? (
                      <div style={{height: "19px"}}></div>
                    ) : (
                      <span className="d-block small text-muted">
                        ({safeCalculate(calculateTotalBalance) > 0 ? (safeCalculate(calculateTotalCash) / safeCalculate(calculateTotalBalance) * 100).toFixed(1) : 0}%)
                      </span>
                    )}
                  </Card.Body>
                </Card>
              </div>
              <div className="col-xl col-lg-4 col-md-6 col-sm-6 pb-3 px-1">
                <Card 
                  className="h-100 portfolios-card"
                >
                  <Card.Body className="p-2 text-center">
                    <Card.Title className="fs-6 mb-1">Today's Gain</Card.Title>
                    {loading ? (
                      <div className="text-muted small" style={{height: "18px"}}></div>
                    ) : (
                      <small className="text-muted d-block">prev bal basis</small>
                    )}
                    {loading ? (
                      <Placeholder as={Card.Text} animation="glow" className="mb-0">
                        <Placeholder xs={7} className="fs-5" />
                      </Placeholder>
                    ) : (
                      (() => {
                        const amount = normalizeNearZero(portfolioTotals.today_gain_amount || 0);
                        return (
                          <Card.Text className={`fs-5 mb-0 fw-bold text-${amount >= 0 ? 'success' : 'danger'}`}>
                            {formatBalanceDisplay(amount)}
                          </Card.Text>
                        );
                      })()
                    )}
                    {loading ? (
                      <div style={{height: "19px"}}></div>
                    ) : (
                      (() => {
                        const pct = normalizeNearZero(portfolioTotals.today_gain_percentage || 0);
                        return (
                          <span className={`d-block small text-${pct >= 0 ? 'success' : 'danger'}`}>
                            ({Math.abs(pct).toFixed(2)}%)
                          </span>
                        );
                      })()
                    )}
                  </Card.Body>
                </Card>
              </div>
              <div className="col-xl col-lg-6 col-md-6 col-sm-6 pb-3 px-1">
                <Card 
                  className="h-100 portfolios-card"
                >
                  <Card.Body className="p-2 text-center">
                    <Card.Title className="fs-6 mb-1">Total Gain</Card.Title>
                    <div className="text-muted small" style={{height: "18px"}}></div>
                    {loading ? (
                      <Placeholder as={Card.Text} animation="glow" className="mb-0">
                        <Placeholder xs={7} className="fs-5" />
                      </Placeholder>
                    ) : (
                      <Card.Text className={`fs-5 mb-0 fw-bold text-${safeCalculate(calculateOverallGain) >= 0 ? 'success' : 'danger'}`}>
                        ${safeCalculate(calculateOverallGain).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Card.Text>
                    )}
                    {loading ? (
                      <div style={{height: "19px"}}></div>
                    ) : (
                      <span className={`d-block small text-${safeCalculate(calculateOverallGain) >= 0 ? 'success' : 'danger'}`}>
                        ({safeCalculate(calculateOverallGainPercentage).toFixed(2)}%)
                      </span>
                    )}
                  </Card.Body>
                </Card>
              </div>
              <div className="col-xl col-lg-6 col-md-6 col-sm-6 pb-3 px-1">
                <Card 
                  className="h-100 portfolios-card"
                >
                  <Card.Body className="p-2 text-center">
                    <Card.Title className="fs-6 mb-1">Year-to-Date</Card.Title>
                    {loading ? (
                      <div className="text-muted small" style={{height: "18px"}}></div>
                    ) : (
                      processedPortfolios.some(p => p.ytd_start_date) ? (
                        <small className="text-muted d-block">since {new Date(processedPortfolios.filter(p => p.ytd_start_date)[0].ytd_start_date).toLocaleDateString()}</small>
                      ) : (
                        <div className="text-muted small" style={{height: "18px"}}></div>
                      )
                    )}
                    {loading ? (
                      <Placeholder as={Card.Text} animation="glow" className="mb-0">
                        <Placeholder xs={7} className="fs-5" />
                      </Placeholder>
                    ) : (
                      <Card.Text className={`fs-5 mb-0 fw-bold text-${portfolioTotals.ytd_gain_amount >= 0 ? 'success' : 'danger'}`}>
                        ${portfolioTotals.ytd_gain_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Card.Text>
                    )}
                    {loading ? (
                      <div style={{height: "19px"}}></div>
                    ) : (
                      <span className={`d-block small text-${portfolioTotals.ytd_gain_amount >= 0 ? 'success' : 'danger'}`}>
                        ({portfolioTotals.ytd_gain_percentage.toFixed(2)}%)
                      </span>
                    )}
                  </Card.Body>
                </Card>
              </div>
            </Col>
          </Row>
          
          <Row className="mb-3">
            <Col>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <Button 
                    variant="outline-primary"
                    onClick={() => setShowBalanceHistoryModal(true)}
                    size="sm"
                    className="me-2"
                  >
                    <i className="bi bi-graph-up"></i> Balance
                  </Button>
                  <Button 
                    variant="outline-primary"
                    onClick={() => setShowPieChartModal(true)}
                    size="sm"
                    className="me-2"
                  >
                    <i className="bi bi-pie-chart"></i> Pie Chart
                  </Button>
                  <Button 
                    variant="outline-primary"
                    onClick={() => setShowCreateModal(true)}
                    size="sm"
                    className="me-2"
                  >
                    <i className="bi bi-plus"></i> Portfolio
                  </Button>
                </div>
                
                {/* View toggle and Column configuration dropdown */}
                <div className="d-flex gap-2">
                  {/* View mode toggle */}
                  <div className="btn-group" role="group">
                    <Button
                      variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
                      size="sm"
                      onClick={() => setViewMode('table')}
                      disabled={loading}
                    >
                      <i className="bi bi-table"></i> Table
                    </Button>
                    <Button
                      variant={viewMode === 'cards' ? 'primary' : 'outline-primary'}
                      size="sm"
                      onClick={() => setViewMode('cards')}
                      disabled={loading}
                    >
                      <i className="bi bi-grid-3x3-gap"></i> Cards
                    </Button>
                  </div>
                  
                  {/* Column configuration dropdown - only show for table view */}
                  {viewMode === 'table' && visibleColumns && (
                    <ColumnVisibilityDropdown
                      visibleColumns={visibleColumns}
                      columns={Object.keys(visibleColumns)}
                      onToggleColumn={(column, newValue) => {
                        setVisibleColumns(prev => ({
                          ...prev,
                          [column]: newValue
                        }));
                      }}
                      idPrefix="header-column-toggle"
                      disabled={loading}
                    >
                      <Dropdown.Divider />
                      <div className="text-muted small">
                        <div>• Click column headers to sort</div>
                      </div>
                      <Dropdown.Divider />
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="w-100"
                        onClick={() => {
                          // Reset to defaults
                          setColumnOrder(defaultColumnOrder);
                          setVisibleColumns({
                            portfolio: true,
                            description: true,
                            balance: true,
                            bottom_line: true,
                            cash: true,
                            today_gain_amount: true,
                            today_gain_percentage: true,
                            total_gain_amount: true,
                            total_gain_percentage: true,
                            ytd_gain_amount: true,
                            ytd_gain_percentage: true
                          });
                          // Clear localStorage
                          localStorage.removeItem(`columnOrder-dashboard`);
                          localStorage.removeItem(`visibleColumns-dashboard`);
                        }}
                      >
                        Reset Columns
                      </Button>
                    </ColumnVisibilityDropdown>
                  )}
                </div>
              </div>
            </Col>
          </Row>
          
          {/* Render table or cards based on view mode */}
          {viewMode === 'table' ? (
            <div className="portfolios-table-container">
              <div className="table-responsive">
                <AccountPortfolioTable 
                  items={processedPortfolios} 
                  type="portfolio" 
                  showTotal={true}
                  onEditName={handleEditName}
                  onEditDescription={handleEditDescription}
                  tableLoading={tableLoading}
                  totalData={{
                    name: "Total",
                    balance: portfolioTotals.balance,
                    bottom_line: safeCalculate(calculateTotalBottomLine),
                    cash: portfolioTotals.cash,
                    today_gain_amount: portfolioTotals.today_gain_amount,
                    today_gain_percentage: portfolioTotals.today_gain_percentage,
                    total_gain_amount: portfolioTotals.total_gain_amount,
                    total_gain_percentage: portfolioTotals.total_gain_percentage,
                    ytd_gain_amount: portfolioTotals.ytd_gain_amount,
                    ytd_gain_percentage: portfolioTotals.ytd_gain_percentage
                  }}
                  visibleColumns={visibleColumns}
                  columnOrder={columnOrder}
                  className="portfolios-table"
                />
              </div>
            </div>
          ) : (
            <div className="portfolios-cards-container">
              {tableLoading ? (
                <Row>
                  {[1, 2, 3, 4, 5, 6].map(index => (
                    <div key={index} className="col-lg-4 col-md-6 col-sm-12 mb-2">
                      <Card className="h-100 portfolios-card portfolio-card">
                        <Card.Body className="p-2">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <div className="flex-grow-1">
                              <Placeholder as={Card.Title} animation="glow" className="mb-0">
                                <Placeholder xs={7} />
                              </Placeholder>
                              <Placeholder as={Card.Subtitle} animation="glow">
                                <Placeholder xs={5} />
                              </Placeholder>
                            </div>
                            <Placeholder animation="glow">
                              <Placeholder xs={3} />
                            </Placeholder>
                          </div>
                          <div className="row g-1 mb-1">
                            <div className="col-6">
                              <Placeholder animation="glow">
                                <Placeholder xs={12} />
                              </Placeholder>
                            </div>
                            <div className="col-6">
                              <Placeholder animation="glow">
                                <Placeholder xs={12} />
                              </Placeholder>
                            </div>
                          </div>
                          <div className="row g-1">
                            <div className="col-4">
                              <Placeholder animation="glow" className="text-center">
                                <Placeholder xs={10} />
                              </Placeholder>
                            </div>
                            <div className="col-4">
                              <Placeholder animation="glow" className="text-center">
                                <Placeholder xs={10} />
                              </Placeholder>
                            </div>
                            <div className="col-4">
                              <Placeholder animation="glow" className="text-center">
                                <Placeholder xs={10} />
                              </Placeholder>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </div>
                  ))}
                </Row>
              ) : (
                <Row>
                  {processedPortfolios.map(portfolio => (
                    <PortfolioCard
                      key={portfolio.portfolio_id}
                      portfolio={portfolio}
                    />
                  ))}
                </Row>
              )}
            </div>
          )}
          
          {/* Pie Chart Modal */}
          <PieChartModal
            show={showPieChartModal}
            onHide={() => setShowPieChartModal(false)}
            portfolios={filteredPortfolios}
            cash={0}
          />
          
          {/* Balance History Modal */}
          <BalanceHistoryModal
            show={showBalanceHistoryModal}
            onHide={() => setShowBalanceHistoryModal(false)}
            isAggregated={true}
            currentBalance={calculateTotalBalance()}
            isBalanceLoading={tableLoading}
          />
        </>
      ) : (
        // Only show this when we're done loading and there are no portfolios
        <div className="text-center my-5">
          <p className="mb-4">You don't have any portfolios yet.</p>
          <Button 
            variant="outline-primary"
            onClick={() => setShowCreateModal(true)}
            size="sm"
          >
            <i className="bi bi-plus"></i> Portfolio
          </Button>
        </div>
      )}

      {/* Create Portfolio Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Portfolio</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {createError && (
            <Alert variant="danger">{createError}</Alert>
          )}
          <Form onSubmit={handleCreatePortfolio}>
            <Form.Group className="mb-3" controlId="portfolioName">
              <Form.Label>Portfolio Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter portfolio name"
                value={newPortfolioName}
                onChange={(e) => setNewPortfolioName(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="portfolioDescription">
              <Form.Label>Description (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter portfolio description"
                value={newPortfolioDescription}
                onChange={(e) => setNewPortfolioDescription(e.target.value)}
              />
            </Form.Group>
            <div className="d-grid gap-2">
              <Button 
                variant="primary" 
                type="submit"
                disabled={createLoading}
                size="sm"
              >
                {createLoading ? 'Creating...' : 'Create Portfolio'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Portfolio Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            Edit Portfolio {editType === 'name' ? 'Name' : 'Description'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editError && (
            <Alert variant="danger">{editError}</Alert>
          )}
          <Form onSubmit={handleSaveEdit}>
            <Form.Group className="mb-3" controlId="editPortfolioField">
              <Form.Label>
                {editType === 'name' ? 'Portfolio Name' : 'Description'}
              </Form.Label>
              {editType === 'name' ? (
                <Form.Control
                  type="text"
                  placeholder="Enter portfolio name"
                  value={editPortfolioValue}
                  onChange={(e) => setEditPortfolioValue(e.target.value)}
                  required
                />
              ) : (
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter portfolio description"
                  value={editPortfolioValue}
                  onChange={(e) => setEditPortfolioValue(e.target.value)}
                />
              )}
            </Form.Group>
            <div className="d-grid gap-2">
              <Button 
                variant="primary" 
                type="submit"
                disabled={editLoading}
                size="sm"
              >
                {editLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
}

export default Dashboard; 
